/* Hero-only integration. The native storefront shell stays outside this component. */
(()=>{
 'use strict';
 if(customElements.get('tt-film-hero'))return;
 const pageDocument=document;
 class TTFilmHero extends HTMLElement{
  async connectedCallback(){
   if(this._cleanup)return;
   this.removeAttribute('data-ready');
   const template=this.querySelector('template[data-hero-template]');if(!template)return;
   const abort=new AbortController(),timers=new Set(),frames=new Set(),observers=new Set();
   const nativeMedia=window.matchMedia.bind(window),root=this.shadowRoot||this.attachShadow({mode:'open'});
   root.replaceChildren();
   const slot=pageDocument.createElement('slot');slot.name='fallback';root.append(slot);
   const player=pageDocument.createElement('div');player.hidden=true;player.append(template.content.cloneNode(true));root.append(player);
   const listen=(target,type,callback,options={})=>target.addEventListener(type,callback,{...(typeof options==='boolean'?{capture:options}:options),signal:abort.signal});
   const trackedTimeout=(fn,ms)=>{if(abort.signal.aborted)return 0;const id=window.setTimeout(()=>{timers.delete(id);if(!abort.signal.aborted)fn()},ms);timers.add(id);return id};
   const trackedFrame=fn=>{if(abort.signal.aborted)return 0;const id=window.requestAnimationFrame(now=>{frames.delete(id);if(!abort.signal.aborted)fn(now)});frames.add(id);return id};
   const cancelTimer=id=>{timers.delete(id);window.clearTimeout(id)},cancelFrame=id=>{frames.delete(id);window.cancelAnimationFrame(id)};
   class ScopedResizeObserver extends ResizeObserver{constructor(callback){super(callback);observers.add(this)}}
   const destination=()=>pageDocument.getElementById(this.dataset.destination)||this.closest('.shopify-section')?.nextElementSibling;
   const scopedDocument={
    querySelector:q=>root.querySelector(q)||(q==='.site-motion-setting'?pageDocument.querySelector(q):null),querySelectorAll:q=>root.querySelectorAll(q),
    getElementById:id=>root.getElementById(id)||(id==='fluid-systems'?destination():pageDocument.getElementById(id)),
    get hidden(){return pageDocument.hidden},body:this,documentElement:this,fonts:pageDocument.fonts,
    addEventListener:(type,fn,options)=>listen(pageDocument,type,fn,options),
    dispatchEvent:e=>pageDocument.dispatchEvent(e)
   };
   const scopedWindowEvents=(type,fn,options)=>listen(['keydown','wheel','touchstart','touchmove','touchend','touchcancel','pointerdown'].includes(type)?root:window,type,fn,options);
   const scopedMatchMedia=q=>{const media=nativeMedia(q);return {get matches(){return media.matches},addEventListener:(type,fn,options)=>listen(media,type,fn,options)}};
   this._cleanup=()=>{abort.abort();timers.forEach(id=>window.clearTimeout(id));frames.forEach(id=>window.cancelAnimationFrame(id));observers.forEach(o=>o.disconnect());root.querySelectorAll('video').forEach(v=>{v.pause();v.removeAttribute('src');v.load()});this._cleanup=null};
   const stylesReady=await Promise.all([...root.querySelectorAll('link[rel="stylesheet"]')].map(link=>new Promise(resolve=>{
    if(link.sheet)return resolve(true);
    const timeout=trackedTimeout(()=>resolve(false),5000);
    const finish=value=>{cancelTimer(timeout);resolve(value)};
    listen(link,'load',()=>finish(true),{once:true});listen(link,'error',()=>finish(false),{once:true});
   })));
   if(abort.signal.aborted||stylesReady.some(ready=>!ready))return;
   const nativeHeader=pageDocument.querySelector('#site-header,.header-section');
   const updateHeader=()=>{const box=nativeHeader?.getBoundingClientRect();const height=box?Math.max(0,Math.round(box.bottom)):0;const value=height+'px';if(this.style.getPropertyValue('--header')!==value)this.style.setProperty('--header',value)};
   updateHeader();if(nativeHeader)new ScopedResizeObserver(updateHeader).observe(nativeHeader);
   // The native announcement strip scrolls away before Hyper pins its header.
   // Account for that strip so the playback controls fit the first viewport.
   let headerFrame=0;
   listen(window,'scroll',()=>{if(!headerFrame)headerFrame=trackedFrame(()=>{headerFrame=0;updateHeader()})},{passive:true});
   const story=root.querySelector('.journey');if(story&&window.Shopify?.designMode)story.dataset.autoplay='false';
   // Replace the fallback before measuring or requesting inline playback. The
   // first video must occupy the visible hero, not sit below a second hero.
   slot.remove();player.hidden=false;
   try{bootHero(scopedDocument,scopedWindowEvents,scopedMatchMedia,trackedTimeout,cancelTimer,trackedFrame,cancelFrame,ScopedResizeObserver)}
   catch(error){player.hidden=true;root.prepend(slot);this._cleanup();console.error('Tech Tool hero could not initialize; showing the static hero.',error);return}
   this.setAttribute('data-ready','');
   // Observe native drawer state only. Shopify Inbox is intentionally excluded.
   const drawers=[...pageDocument.querySelectorAll('cart-drawer,menu-drawer,drawer-component,search-drawer,dialog,.header-section details')];
   let overlayOpen=false;
   // Mobile navigation contains default-open accordions even while its drawer is
   // closed. Only visible desktop details count as an active overlay.
   const syncDrawers=()=>{const open=drawers.some(el=>el.hasAttribute('open')&&(el.tagName!=='DETAILS'||el.getClientRects().length>0));if(open!==overlayOpen){overlayOpen=open;pageDocument.dispatchEvent(new CustomEvent('site:overlay',{detail:{open}}))}};
   const drawerObserver=new MutationObserver(syncDrawers);observers.add(drawerObserver);
   drawers.forEach(el=>drawerObserver.observe(el,{attributes:true,attributeFilter:['open']}));listen(window,'resize',syncDrawers,{passive:true});syncDrawers();
   listen(pageDocument,'shopify:block:select',event=>{
    if(!window.Shopify?.designMode||!(this.closest('.shopify-section')||this).contains(event.target))return;
    const button=[...root.querySelectorAll('[data-block-id]')].find(el=>el.dataset.blockId===event.detail?.blockId);
    button?.click();
   });
   listen(window,'pagehide',()=>{root.querySelector('.play-scene[aria-pressed="true"]')?.click();root.querySelectorAll('video').forEach(v=>v.pause())});
  }
  disconnectedCallback(){this._cleanup?.()}
 }
 function bootHero(document,addEventListener,matchMedia,setTimeout,clearTimeout,requestAnimationFrame,cancelAnimationFrame,ResizeObserver){
  (() => {
 'use strict';
 const journey=document.querySelector('.journey');if(!journey)return;
 const stage=journey.querySelector('.stage');
 const films=[...document.querySelectorAll('.film')],windows=films.map(f=>f.querySelector('.film-window'));
 const videos=films.map(f=>f.querySelector('video')),panels=[...document.querySelectorAll('.copy-moment')];
 const chapters=[...document.querySelectorAll('[data-chapter]')],label=document.querySelector('.current-chapter');
 const chapterNav=document.querySelector('.chapters');
 const chapterTracks=chapters.map(button=>button.querySelector('.chapter-track--base i'));
 const posters=films.map(f=>f.querySelector('img')),articles=panels.map(p=>p.querySelector('article'));
 const paint=(element,key,value)=>{value=String(value);if(element.style[key]!==value)element.style[key]=value};
 const labelCurrent=label.querySelector('.chapter-current'),labelIncoming=label.querySelector('.chapter-incoming');
 const toggle=document.querySelector('.site-motion-setting'),play=document.querySelector('.play-scene'),cue=document.querySelector('.scroll-cue');
 const stills=document.querySelector('.still-story'),mediaQuery=matchMedia('(prefers-reduced-motion: reduce)'),chapterSelect=document.querySelector('.chapter-select'),notice=document.querySelector('.media-notice');
 const order=films.map((_,i)=>i),names=chapters.map(button=>button.getAttribute('aria-label'));
 const clamp=(n,a=0,b=1)=>Math.max(a,Math.min(b,n)),mix=(a,b,t)=>a+(b-a)*t;
 const ease=(v)=>{const t=clamp(v);return t*t*t*(t*(t*6-15)+10)};
 let reduced=mediaQuery.matches,raf=0,previousTime=0,target=0,current=0,shown=0,manualPlaying=false,activeFilm=0;
 const states=videos.map(()=>({loaded:false,desired:0,start:0,seeks:0,failed:false,candidates:[],sourceIndex:0,native:false,playPending:false,blocked:false,abortRetries:0,retryAfter:0,generation:0,framePending:false,frameRequest:0,frameTimer:0,frameGeneration:0}));
 const LAST=order.length-1,EXTENT=order.length-.001,HANDOFF=.68,LEAD=1-HANDOFF;
 // The shopping-first layout uses native playback at every width. Scrolling stays with the page.
 const coarsePointer=matchMedia('(pointer:coarse)');
 const framedMobile=()=>innerWidth<=1000||(innerWidth<=1366&&coarsePointer.matches);
 const freeScroll=()=>journey.dataset.scrollMode==='page'||framedMobile();
 let mobileOffscreen=false,wasFramed=freeScroll();
 let playingFilm=-1,navigation=null,shellPaused=false,waitingForNext=false;
 let exploreDismissed=false,exploreTimer=0,exploreHideTimer=0,geometry=null,catalogScroll=null;
 let autoplayPending=false,autoplayTimer=0,scrollAnchor=null,scrollSpan=0,lastScrollY=scrollY;
  let handoffEligible=false;
  let settleTimer=0,settling=null,scrollDirection=1;
  const AUTOPLAY_DELAY=1000,CATALOG_SCROLL_DURATION=900,NAVIGATION_DURATION=420;
  const SCROLL_IDLE_DELAY=180,SETTLE_DURATION=420;
 function openingReady(){
  const v=videos[0];
  // Request muted playback before decode on mobile: Safari may defer loading
  // until play(). Keep the poster visible until the decoder supplies a frame.
  if(framedMobile())return true;
  if(v.readyState<2||v.seeking||v.dataset.ready!=='true')return false;
  if(v.readyState<3)return false;
  const needed=Math.min(2,Math.max(0,v.duration-v.currentTime));
  for(let i=0;i<v.buffered.length;i++){
   if(v.buffered.start(i)<=v.currentTime+.05&&v.buffered.end(i)-v.currentTime>=needed)return true;
  }
  return false;
 }
 function cancelHandoff(){handoffEligible=false}
 function cancelAutoplay(){autoplayPending=false;clearTimeout(autoplayTimer);autoplayTimer=0}
 function openingInView(){
  if(!freeScroll())return scrollY<32;
  const box=stage.getBoundingClientRect();
  const visible=Math.max(0,Math.min(innerHeight,box.top+box.height)-Math.max(0,box.top));
  // Page scrolling can begin before media is ready. Start while the hero is
  // visible; the offscreen guard also accounts for the sticky header.
  return box.height>0&&visible>0;
 }
 function scheduleAutoplay(){
  if(!autoplayPending||autoplayTimer||reduced||shellPaused||mobileOffscreen||document.hidden||!openingInView())return;
  if(states[0].failed){cancelAutoplay();return}
  if(!openingReady())return;
  // Begin mobile playback during setup without a desktop startup delay.
  if(framedMobile()){autoplayPending=false;startPlayback();return}
  autoplayTimer=setTimeout(()=>{
   autoplayTimer=0;
   if(!autoplayPending||reduced||shellPaused||mobileOffscreen||document.hidden||!openingInView())return;
   if(!openingReady())return;
   autoplayPending=false;startPlayback();
  },AUTOPLAY_DELAY);
 }
 const EXPLORE_IDLE_DELAY=5000;
 const cueDesktop=cue.querySelector('.cue-label--desktop'),cueTouch=cue.querySelector('.cue-label--touch');
 cueDesktop.textContent='Scroll to continue';cueTouch.textContent='Explore products';
 if(freeScroll()){cue.hidden=true;cue.inert=true;cue.setAttribute('aria-hidden','true');exploreDismissed=true}
 function showCompletionCue(){
  if(reduced||shellPaused||document.hidden||mobileOffscreen||current<.998)return;
  clearTimeout(exploreTimer);clearTimeout(exploreHideTimer);exploreTimer=exploreHideTimer=0;
  cueDesktop.textContent=cueTouch.textContent='Explore products';
  cue.setAttribute('aria-label','Explore products');cue.classList.add('is-complete');
  cue.classList.remove('is-reminder','is-dismissed');
  cue.hidden=false;cue.inert=false;cue.removeAttribute('aria-hidden');exploreDismissed=false;
 }
 function dismissExploreCue(){
  clearTimeout(exploreTimer);exploreTimer=0;
  if(exploreDismissed)return;
  exploreDismissed=true;cue.inert=true;cue.setAttribute('aria-hidden','true');
  cue.classList.toggle('is-dismissed',true);
  exploreHideTimer=setTimeout(()=>{cue.hidden=true;exploreHideTimer=0},220);
 }
 function canRemindExplore(){
  if(reduced||freeScroll()||shellPaused||document.hidden||navigation||catalogScroll)return false;
  const b=bounds();
  return scrollY>=b.top-2&&scrollY<=b.top+b.range+2;
 }
 function showExploreCue(){
  if(!canRemindExplore())return;
  clearTimeout(exploreTimer);clearTimeout(exploreHideTimer);exploreTimer=exploreHideTimer=0;
  cue.hidden=false;cue.inert=false;cue.removeAttribute('aria-hidden');
  cue.classList.toggle('is-dismissed',false);exploreDismissed=false;
 }
 function scheduleExploreCue(){
  clearTimeout(exploreTimer);exploreTimer=0;
  if(!exploreDismissed||!canRemindExplore())return;
  // One idle deadline, not a polling loop or extra animation-frame work.
  exploreTimer=setTimeout(()=>{
   exploreTimer=0;if(!canRemindExplore())return;
   clearTimeout(exploreHideTimer);exploreHideTimer=0;
   cueDesktop.textContent='Scroll to continue';cueTouch.textContent='Explore products';
   cue.classList.toggle('is-reminder',true);cue.hidden=false;cue.inert=false;
   cue.removeAttribute('aria-hidden');cue.classList.toggle('is-dismissed',false);exploreDismissed=false;
  },EXPLORE_IDLE_DELAY);
 }
 function resetExploreCue(){dismissExploreCue();scheduleExploreCue()}
 let labelWidths=[],chapterShapes=chapters.map((_,i)=>i===0?1:0),chapterProgress=chapters.map(()=>null);
 function measureChapters(){
  // Label measurements happen at setup/resize only. Dot morphing needs no layout reads.
  const probe=labelCurrent.cloneNode(false);
  probe.style.cssText='position:absolute;visibility:hidden;width:max-content;transform:none;pointer-events:none';
  label.append(probe);
  labelWidths=names.map((name,i)=>{probe.textContent=`0${i+1} / ${name}`;return probe.getBoundingClientRect().width});
  probe.remove();
 }
 function drawChapterDots(from,to,progress){
  paintChapterDots(chapters.map((_,j)=>(j===from?1-progress:0)+(j===(to??from)?progress:0)));
 }
 function paintChapterDots(shapes){
  // A constant total expansion keeps the dock still while one dot opens and the other closes.
  // Capture the fractional shapes on interruption, so repeated clicks never snap the controls.
  chapterShapes=shapes;
  chapters.forEach((button,j)=>{
   const value=clamp(shapes[j]).toFixed(5);
   if(button.style.getPropertyValue('--chapter-active')!==value)button.style.setProperty('--chapter-active',value);
  });
 }
 function setChapterProgress(j,value){
  value=clamp(value);if(chapterProgress[j]===value)return;
  chapterProgress[j]=value;
  // Keep a round white leading dot at zero, then fill the active track from left to right.
  paint(chapterTracks[j],'width',`calc(${(value*100).toFixed(4)}% + ${((1-value)*8).toFixed(4)}px)`);
 }
 function setSelectedChapter(index){
  if(chapterSelect.value===String(index)&&chapters[index].getAttribute('aria-current')==='step')return;
  if(chapterSelect.value!==String(index))chapterSelect.value=String(index);
  chapters.forEach((button,j)=>{
   if(j===index){if(button.getAttribute('aria-current')!=='step')button.setAttribute('aria-current','step')}
   else if(button.hasAttribute('aria-current'))button.removeAttribute('aria-current');
  });
 }
 const duration=i=>+videos[i].dataset.duration-.10;
 const clipProgress=(i,t)=>clamp((t+(i?LEAD:0))/(1+(i?LEAD:0)));
 // Page playback uses seconds elapsed in the outgoing dissolve, so incoming
 // footage stays at its recorded speed even during the longer opening scene.
 const incomingTime=(i,phase)=>freeScroll()?Math.max(0,phase-HANDOFF)*duration(i)/(1+(i?LEAD:0)):clipProgress(i+1,phase-1)*duration(i+1);
 const incomingRate=i=>freeScroll()?1:(1+(i?LEAD:0))*duration(i+1)/(duration(i)*(1+LEAD));
 function loadFilm(i){
  if(!videos[i]||reduced)return;
  const s=states[i],v=videos[i];
  const poster=posters[i];if(poster.loading!=='eager')poster.loading='eager';
  if(s.loaded||s.failed)return;
  const mobile=innerWidth<=760||framedMobile();
  const hevc={url:mobile?v.dataset.mobileHevc:'',type:'video/mp4; codecs="hvc1"'};
  const mp4={url:mobile?v.dataset.mobile:v.dataset.desktop,type:'video/mp4; codecs="avc1.640028"'};
  const webm={url:mobile?v.dataset.mobileWebm:v.dataset.desktopWebm,type:'video/webm; codecs="vp9"'};
  // Use the smaller same-resolution mobile encode only when the browser
  // advertises HEVC support. Keep H.264 first in the compatibility fallback.
  s.candidates=[hevc,mp4,webm].filter(x=>x.url&&v.canPlayType(x.type));
  if(!s.candidates.length)s.candidates=[mp4,webm].filter(x=>x.url);
  s.sourceIndex=0;s.loaded=true;v.muted=true;v.defaultMuted=true;v.playsInline=true;v.preload='auto';activateSource(i);
 }
 function activateSource(i){
  const v=videos[i],s=states[i];pauseNative(i);v.dataset.ready='false';if(playingFilm===i)playingFilm=-1;
  if(s.sourceIndex>=s.candidates.length){s.failed=true;films[i].dataset.fallback='still';if(i===0)cancelAutoplay();if(i===activeFilm)stopPlayback();wake();return}
  films[i].dataset.fallback='';s.start=0;s.abortRetries=0;s.retryAfter=0;
  v.src=s.candidates[s.sourceIndex].url;v.load();
  // Safari may defer a hidden preload, and a slow connection is not a broken
  // source. Only a real media error advances the source/fallback state. A timer
  // here used to permanently disable later chapters before they were selected.
 }
 function seek(i,time){
  const s=states[i],v=videos[i];
  // Seek inside a frame, not on its timestamp boundary. Browser timestamp
  // truncation can otherwise display the preceding frame and repeat/jump frames.
  const frame=Math.round(clamp(time,0,Math.max(0,+v.dataset.duration-.10))*24);
  s.desired=frame===0?0:frame/24+.001;
  const targetTime=s.desired.toFixed(3);if(v.dataset.targetTime!==targetTime)v.dataset.targetTime=targetTime;
  pump(i);
 }
 function clearPresentation(i){
  const s=states[i],v=videos[i];s.frameGeneration++;
  if(s.frameRequest&&v.cancelVideoFrameCallback)v.cancelVideoFrameCallback(s.frameRequest);
  clearTimeout(s.frameTimer);s.framePending=false;s.frameRequest=s.frameTimer=0;
 }
 function awaitPresentation(i,time){
  const s=states[i],v=videos[i];
  if(!v.requestVideoFrameCallback||films[i].hidden)return;
  s.framePending=true;const generation=++s.frameGeneration;
  const release=()=>{
   if(generation!==s.frameGeneration)return;
   clearPresentation(i);pump(i);wake();
  };
  const presented=(now,metadata)=>{
   if(generation!==s.frameGeneration)return;
   if(Math.abs(metadata.mediaTime-time)<.045)release();
   else s.frameRequest=v.requestVideoFrameCallback(presented);
  };
  s.frameRequest=v.requestVideoFrameCallback(presented);
  // Offscreen/occluded films may not receive presentation callbacks. Never lock
  // navigation on one missing callback; seeked still guards an unfinished decode.
  s.frameTimer=setTimeout(release,100);
 }
 function pump(i){
  const v=videos[i],s=states[i];
  if(reduced||shellPaused||document.hidden||s.failed||!s.loaded||v.readyState<1||v.seeking||s.framePending||(s.native||(manualPlaying&&i===activeFilm)))return;
  if(Math.abs(v.currentTime-s.desired)<.028)return;
  s.start=performance.now();awaitPresentation(i,s.desired);v.currentTime=s.desired;
 }
 videos.forEach((v,i)=>{
  v.addEventListener('loadedmetadata',()=>{
   const s=states[i];
   // A gesture can request play before Safari exposes metadata. Align that
   // one entry seek now, rather than letting the new chapter restart at zero.
   if(s.native&&Math.abs(v.currentTime-s.desired)>.06)v.currentTime=s.desired;
   else pump(i);
  });
  v.addEventListener('playing',()=>{if(states[i].native&&v.readyState>=2){states[i].abortRetries=0;states[i].retryAfter=0;v.dataset.ready='true';syncPlaybackControl();wake()}});
  v.addEventListener('pause',()=>{
   const s=states[i];
   // A browser visibility pause can leave play() pending. Retire that request
   // so returning to this clip can actually call play() again. Ignore queued
   // pause events from an earlier request when the video is already running.
   if(v.paused&&s.native){s.generation++;s.native=false;s.playPending=false}
   syncPlaybackControl();if(manualPlaying||navigation?.resume)wake();
  });
  v.addEventListener('loadeddata',()=>{
   pump(i);
   // A first decode can arrive before a pending chapter seek. Keep the poster
   // until the requested frame is ready instead of flashing the opening frame.
   if(!v.seeking&&(states[i].native||Math.abs(v.currentTime-states[i].desired)<.06))v.dataset.ready='true';
   scheduleAutoplay();wake();
  });
  v.addEventListener('seeked',()=>{
   const s=states[i];
   if(s.start){v.dataset.lastSeekMs=String(Math.round(performance.now()-s.start));v.dataset.seekCount=String(++s.seeks)}
   // Once revealed, retain the last decoded image while scroll targets advance.
   // Hiding it on every target mismatch flashes the opening poster between seeks.
   // Source changes and hidden chapter navigation reset readiness explicitly.
   pump(i);
   if(!v.seeking&&Math.abs(v.currentTime-s.desired)<.06)v.dataset.ready='true';
   scheduleAutoplay();wake();
  });
  for(const event of ['canplay','progress','canplaythrough'])v.addEventListener(event,scheduleAutoplay);
  v.addEventListener('error',()=>{const s=states[i];s.sourceIndex++;activateSource(i)});
 });
 function pauseNative(i){
  const s=states[i];clearPresentation(i);s.generation++;s.native=false;s.playPending=false;
  videos[i].autoplay=false;videos[i].pause();
 }
 function notifyPlayback(){document.dispatchEvent(new CustomEvent('site:playback',{detail:{playing:manualPlaying}}))}
 function mediaIsPlaying(){return states.some((s,i)=>s.native&&!videos[i].paused)}
 function syncPlaybackControl(){
  // Playback intent survives an offscreen pause; the button reflects the media
  // actually running, so a returning visitor's first Play tap always starts it.
  const running=!!((manualPlaying||navigation?.resume)&&!mobileOffscreen&&!shellPaused&&mediaIsPlaying());
  const action=running?'Pause story':current>=.998?'Replay story':'Play story';
  if(play.getAttribute('aria-label')!==action){play.setAttribute('aria-label',action);play.title=action}
  if(play.getAttribute('aria-pressed')!==String(running))play.setAttribute('aria-pressed',String(running));
 }
 function stopPlayback(anchorY=scrollY){
  cancelHandoff();
  if(!manualPlaying&&!navigation?.resume&&!mediaIsPlaying())return;
  if(navigation)navigation.resume=false;
  manualPlaying=false;playingFilm=-1;videos.forEach((v,i)=>{pauseNative(i);v.loop=false});
  anchorScroll(anchorY);
  syncPlaybackControl();notifyPlayback();showExploreCue();
 }
 function startNative(i,time,rate=1,requestBeforeDecode=false){
  const v=videos[i],s=states[i];
  if(shellPaused||document.hidden||mobileOffscreen||s.failed)return false;
  if(performance.now()<s.retryAfter)return false;
  // One pending play request per clip, including while Safari is still loading.
  if(s.playPending)return true;
  if(!requestBeforeDecode&&(v.readyState<2||v.seeking))return false;
  // Safari can pause an inline video as its compositing layer changes visibility.
  // Our playback intent must not mistake that browser pause for a running decoder.
  if(s.native&&!v.paused){if(Math.abs(v.playbackRate-rate)>.001)v.playbackRate=rate;return true}
  if(s.native){s.native=false;s.generation++}
  // One alignment seek on entry/resume; never seek a clip while it is playing.
  if(v.readyState>=1&&Math.abs(v.currentTime-time)>.06){s.desired=time;v.currentTime=time;if(!requestBeforeDecode)return false}
  clearPresentation(i);s.native=true;s.blocked=false;v.loop=false;v.playbackRate=rate;
  v.muted=true;v.defaultMuted=true;v.playsInline=true;v.autoplay=true;
  const generation=++s.generation;
  s.playPending=true;
  const attempt=v.play();
  if(attempt?.then)attempt.then(()=>{
   if(generation!==s.generation)return;
   s.playPending=false;delete journey.dataset.playbackError;
   if(v.readyState>=2&&!v.seeking)v.dataset.ready='true';
   syncPlaybackControl();wake();
  },error=>{
   if(generation!==s.generation)return;
   s.native=false;s.playPending=false;v.autoplay=false;
   // A seek or a visibility change can interrupt a pending play. That abort
   // does not revoke the visitor's playback intent. Retry it at most twice,
   // spaced apart; an actual permission refusal still requires a fresh tap.
   if(error?.name==='AbortError'&&(manualPlaying||navigation?.resume)&&s.abortRetries<2){
    s.abortRetries++;s.retryAfter=performance.now()+150;s.blocked=false;
    syncPlaybackControl();wake();return;
   }
   s.blocked=true;
   journey.dataset.playbackError=error?.name||'PlaybackError';
   // The incoming layer may still be transparent or covered by the outgoing
   // scene. Keep this selection's intent for one fresh request after the fade.
   // A refusal on that fully visible request still exposes the Play control.
   if(navigation?.to===i&&navigation.resume&&freeScroll()){wake();return}
   if(manualPlaying||navigation?.resume){stopPlayback();wake()}
  });else s.playPending=false;
  return true;
 }
 function playCurrent(){
  if(waitingForNext)return;
  if(startNative(activeFilm,states[activeFilm].desired))playingFilm=activeFilm;
 }
 function headlineBaseline(heading){
  // An empty inline box ends exactly on the last text baseline. Its zero width
  // preserves wrapping, and its temporary presence is limited to layout sync.
  const marker=heading.ownerDocument.createElement('span');
  marker.style.cssText='display:inline-block;width:0;height:0;padding:0;margin:0;border:0;vertical-align:baseline';
  heading.append(marker);
  const baseline=marker.getBoundingClientRect().top-heading.closest('article').getBoundingClientRect().top;
  marker.remove();return baseline;
 }
 function syncCopyRows(){
  // Measure only on setup, font readiness and resize, never during playback.
  // A common row height keeps short closing copy from lifting its buttons.
  for(const container of [document.querySelector('.copy-layer'),stills]){
   if(!container||!container.offsetWidth)continue;
   const rows=[['title',':is(h1,h2)'],['body','.tt-journey__body'],['action','.tt-journey__actions']];
   rows.forEach(([name])=>container.style.setProperty('--scene-'+name+'-row','0px'));
   const sizes=rows.map(([name,selector])=>[name,Math.max(0,...[...container.querySelectorAll('.tt-journey__panel '+selector)].map(el=>el.getBoundingClientRect().height))]);
   sizes.forEach(([name,height])=>container.style.setProperty('--scene-'+name+'-row',height.toFixed(3)+'px'));
   container.style.setProperty('--closing-baseline-offset','0px');
   if(innerWidth<=760){
    const reference=container.querySelector('[data-key="scale"] h2, #still-3 h2');
    const closing=container.querySelector('.tt-journey__panel--final h2');
    if(reference&&closing){
     const offset=headlineBaseline(reference)-headlineBaseline(closing);
     container.style.setProperty('--closing-baseline-offset',Math.max(0,offset).toFixed(3)+'px');
    }
   }
  }
 }
 function refreshBounds(){
  // Read layout on lifecycle changes, never inside the animation/scroll loop.
  const height=stage.offsetHeight,top=scrollY+journey.getBoundingClientRect().top-parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header'));
  if(!reduced){
   if(!scrollAnchor)scrollSpan=Math.max(1,journey.offsetHeight-height);
   else if(geometry?.height>0&&height!==geometry.height)scrollSpan*=height/geometry.height;
   if(scrollAnchor)paint(journey,'height',(height+Math.max(0,scrollAnchor.y-top)+(scrollAnchor.remaining??(1-scrollAnchor.position))*scrollSpan).toFixed(3)+'px');
  }
  geometry={top,range:Math.max(0,journey.offsetHeight-height),height};
  syncPageVisibility();
  // Native Shopify headers can shrink as their announcement bar scrolls away.
  // Refresh the anchor target on layout changes, not inside the frame loop.
  if(catalogScroll){const section=document.getElementById('fluid-systems');if(section)catalogScroll.to=Math.max(0,scrollY+section.getBoundingClientRect().top-(parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header'))||0))}
 }
  function bounds(){if(!geometry)refreshBounds();return geometry}
 function syncPageVisibility(){
  if(!freeScroll()||reduced||!geometry)return;
  const b=geometry,offscreen=scrollY>=b.top+b.height||scrollY+innerHeight<b.top;
  if(offscreen===mobileOffscreen)return;
  mobileOffscreen=offscreen;previousTime=0;
  if(offscreen){
   // Defer an unstarted autoplay until return; explicit Pause still cancels it.
   clearTimeout(autoplayTimer);autoplayTimer=0;
   videos.forEach((_,i)=>pauseNative(i));playingFilm=-1;
   cancelAnimationFrame(raf);raf=0;
  }else{wake();scheduleAutoplay()}
  syncPlaybackControl();
 }
  function cancelSettle(){
   clearTimeout(settleTimer);settleTimer=0;
   if(settling){settling=null;target=current}
  }
  function restoreScrollTimeline(anchorY=scrollY,delta=scrollY-anchorY){
   if(reduced||freeScroll()||!scrollAnchor)return;
   const b=bounds();
   if(anchorY<b.top-2||anchorY>b.top+b.range+2)return;
   // Autoplay/chapter clicks retire watched scroll distance. Restore that distance
   // under the pinned stage before visitor scrolling takes over, preserving both
   // the composed frame and the first input delta. Earlier scenes remain reachable.
   scrollAnchor=null;
   paint(journey,'height',(b.height+scrollSpan).toFixed(3)+'px');geometry={...b,range:scrollSpan};
   scrollTo({top:Math.max(0,b.top+current*scrollSpan+delta),behavior:'instant'});
   lastScrollY=scrollY;
  }
  function scheduleSettle(){
   clearTimeout(settleTimer);settleTimer=0;
   if(freeScroll()||manualPlaying||!canRemindExplore())return;
   settleTimer=setTimeout(()=>{
    settleTimer=0;
    if(freeScroll()||manualPlaying||!canRemindExplore())return;
    const position=target*EXTENT,index=Math.min(LAST,Math.floor(position+1e-7)),local=Math.max(0,position-index);
    let landing=target;
    // Only settle the copy entrance/exit and dissolve. Ordinary paused footage
    // stays exactly where the visitor left it; reverse gestures favor the prior scene.
    if(index>0&&local<.17)landing=(index+.17)/EXTENT;
    else if(index<LAST&&local>.55)landing=(scrollDirection<0?index+.5:index+1+.17)/EXTENT;
    if(Math.abs(landing-target)<.000045)return;
    restoreScrollTimeline();
    settling={from:current,to:landing,elapsed:0};wake();
   },SCROLL_IDLE_DELAY);
  }
 function anchorScroll(y=scrollY){
  if(reduced||freeScroll())return;
  const b=bounds();
  scrollAnchor={y:Math.max(b.top,y),position:current};target=current;
  // Retire only the scroll distance already watched. No scrollTo, and no long empty tail
  // after the final scene: the next visitor scroll reveals the original product sections.
  const range=Math.max(0,scrollAnchor.y-b.top)+(1-current)*scrollSpan;
  paint(journey,'height',(b.height+range).toFixed(3)+'px');geometry={...b,range};
 }
 function measure(){
  if(reduced)return;
  const b=bounds();target=freeScroll()?current:scrollAnchor?clamp(scrollAnchor.position+(scrollY-scrollAnchor.y)/scrollSpan):clamp((scrollY-b.top)/Math.max(1,b.range));
  if(!freeScroll()&&(target>.001||exploreDismissed||cue.classList.contains('is-reminder')||scrollY<b.top-2))resetExploreCue();
  wake();
 }
 function wake(){if(!raf&&!shellPaused&&!mobileOffscreen&&!document.hidden&&!reduced)raf=requestAnimationFrame(draw)}
  function scrollToShopping(focusDestination=false){
   cancelSettle();
  cancelAutoplay();stopPlayback();finishNavigation();dismissExploreCue();
  const section=document.getElementById('fluid-systems');
  if(!section||shellPaused||document.hidden)return;
  if(!reduced&&!freeScroll()){
   // Keep the currently composed scene on screen, while removing the unused scroll
   // distance so an early Shop click gets the same short transition as completion.
   const b=bounds(),y=Math.max(b.top,scrollY),range=y-b.top;
   scrollAnchor={y,position:current,remaining:0};target=current;
   paint(journey,'height',(b.height+range).toFixed(3)+'px');geometry={...b,range};
  }
  const header=parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header'))||0;
  const destination=Math.max(0,scrollY+section.getBoundingClientRect().top-header);
  const focus=()=>{if(focusDestination){section.setAttribute('tabindex','-1');section.focus({preventScroll:true})}};
  // Ordinary page mode uses the browser's smooth scroll, so delayed scroll
  // events cannot be mistaken for visitor input and stop the move halfway.
  if(reduced||freeScroll()){scrollTo({top:destination,behavior:reduced?'instant':'smooth'});focus();return}
  // Measure once; scroll with the existing easing and allow immediate interruption.
  catalogScroll={from:scrollY,to:destination,started:null,lastY:scrollY,focus};wake();
 }
 function restoreStoryHistory(){
  if(reduced||freeScroll()||!scrollAnchor)return;
  const b=bounds(),previousHeight=journey.offsetHeight;
  // Restore the full timeline only once the hero is above the viewport. Move the
  // document by the same amount so the visible products stay exactly in place.
  paint(journey,'height',(b.height+scrollSpan).toFixed(3)+'px');
  const restoredHeight=journey.offsetHeight,shift=restoredHeight-previousHeight;
  scrollSpan=restoredHeight-b.height;scrollAnchor=null;geometry={...b,range:scrollSpan};current=target=1;
  if(Math.abs(shift)>.5)scrollTo({top:Math.max(0,scrollY+shift),behavior:'instant'});
  lastScrollY=scrollY;wake();
 }
 function drawCatalogScroll(now){
  const scroll=catalogScroll;
  if(scroll.started===null)scroll.started=now;
  const progress=clamp((now-scroll.started)/CATALOG_SCROLL_DURATION);
  scroll.lastY=mix(scroll.from,scroll.to,ease(progress));
  scrollTo({top:scroll.lastY,behavior:'instant'});lastScrollY=scrollY;
  if(progress===1){catalogScroll=null;restoreStoryHistory();previousTime=0;scroll.focus()}else wake();
 }
 function baseInsets(){return [0,0,0,0]}
 function setWindow(index){
  const inset='0.000% 0.000% 0.000% 0.000%';
  if(windows[index].style.inset!==inset)windows[index].style.inset=inset;
 }
 const filmPose=film=>{
  const value=film.style.transform||'';
  const move=value.match(/translate3d\(([-\d.e+]+)%,([-\d.e+]+)%,0\)/);
  return [move?+move[1]:0,move?+move[2]:0,0,+(value.match(/scale\(([-\d.e+]+)\)/)?.[1]||1)];
 };
 const poseString=p=>`translate3d(${p[0]}%,${p[1]}%,0) scale(${p[3]})`;
 function captureFilms(){
  const snapshots=films.map((film,index)=>({index,weight:0,pose:filmPose(film),alpha:film.hidden?0:clamp(parseFloat(film.style.opacity)||0),z:+film.style.zIndex||0}));
  let remaining=1;
  // Capture the visible mixture, including an interrupted transition, without DOM reads.
  [...snapshots].sort((a,b)=>b.z-a.z||b.index-a.index).forEach(s=>{s.weight=s.alpha*remaining;remaining*=1-s.alpha});
  if(remaining>.001)snapshots[activeFilm].weight+=remaining;
  return snapshots;
 }
 function beginNavigation(index){
  if(navigation?.to===index)return;
  if(!navigation&&chapters[index].getAttribute('aria-current')==='step')return;
  const snapshots=captureFilms(),from=snapshots.reduce((a,b)=>a.weight>=b.weight?a:b).index,dir=index>=from?1:-1;
  const copy=panels.map(panel=>({alpha:panel.hidden?0:clamp(parseFloat(panel.style.opacity)||0),y:+((panel.style.transform||'').match(/translate3d\(0,([-\d.e+]+)px,0\)/)?.[1]||0)}));
  const frameLocal=(videos[index].currentTime/duration(index))*(1+(index?LEAD:0))-(index?LEAD:0);
  const reuseFrame=snapshots[index].weight>.001&&videos[index].readyState>=1&&frameLocal>=(index ? .17 : 0)&&frameLocal<=.54;
  const local=reuseFrame?frameLocal:index===0?0:.31,position=(index+local)/EXTENT;
  navigation={to:index,from,dir,resume:freeScroll(),snapshots,copy,elapsed:0,feedbackElapsed:0,prepareElapsed:0,position,local,labelWidth:parseFloat(label.style.width)||labelWidths[from],label:label.getAttribute('aria-label')||`0${from+1} / ${names[from]}`,progress:[...chapterProgress],dots:[...chapterShapes]};
  if(snapshots[index].weight<.001){snapshots[index].pose=[dir*1.2,.25,-dir*.35,1.035];windows[index].style.inset=baseInsets(index).map(n=>n+'%').join(' ')}
  navigation.inset=(windows[index].style.inset||'0 0 0 0').split(' ').map(parseFloat);
  // Put the destination in the rendered tree before requesting play in the
  // touch event. Its zero opacity preserves the outgoing composition.
  if(films[index].hidden){paint(films[index],'opacity',0);films[index].hidden=false}
  videos.forEach((_,i)=>pauseNative(i));loadFilm(index);
  setSelectedChapter(index);
  // A hidden clip may still contain its old final frame. Keep its poster visible
  // until this request's landing frame has actually decoded.
  if(snapshots[index].weight<.001&&Math.abs(videos[index].currentTime-clipProgress(index,local)*duration(index))>.06)videos[index].dataset.ready='false';
  // Decode one destination frame; don't scrub every intermediate chapter on a click.
  seek(index,clipProgress(index,local)*duration(index));
  current=target=position;shown=index;activeFilm=index;
  anchorScroll();
  stage.classList.toggle('is-navigating',true);dismissExploreCue();wake();
 }
  function finishNavigation(){
   if(!navigation)return;
   const followScroll=navigation.followScroll,index=navigation.to,v=videos[index];
   // Keep the native clock when Play, resize, or another lifecycle event ends
   // a touch-initiated dissolve early.
   if(states[index].native&&v.readyState>=2&&!v.seeking){
    const local=(v.currentTime/duration(index))*(1+(index?LEAD:0))-(index?LEAD:0);
    navigation.position=Math.max(navigation.position,clamp((index+Math.max(0,local))/EXTENT));
   }
   current=target=navigation.position;shown=navigation.to;activeFilm=navigation.to;
   navigation=null;stage.classList.toggle('is-navigating',false);previousTime=0;scheduleExploreCue();
   if(followScroll){measure();scheduleSettle()}
 }
 function drawNavigation(dt){
  const n=navigation,v=videos[n.to],state=states[n.to];
  const destinationReady=v.readyState>=2&&!v.seeking&&(state.native||Math.abs(v.currentTime-state.desired)<.06);
  n.feedbackElapsed+=dt;
  const controlFlow=ease(n.feedbackElapsed/NAVIGATION_DURATION);
  paintChapterDots(n.dots.map((value,j)=>mix(value,j===n.to?1:0,controlFlow)));
  chapters.forEach((_,j)=>setChapterProgress(j,mix(n.progress[j],j<n.to?1:j===n.to?n.local:0,controlFlow)));
  // Give an uncached chapter one short preparation window. Preserve the exact
  // outgoing composition; cached frames start immediately and input can cancel.
  if(n.elapsed===0&&!state.failed&&!destinationReady&&n.prepareElapsed<120){n.prepareElapsed+=dt;wake();return}
  n.elapsed+=dt;
  const u=clamp(n.elapsed/NAVIGATION_DURATION),flow=ease(u);
  let cumulative=0;
  films.forEach((film,j)=>{
   const snapshot=n.snapshots[j],weight=snapshot.weight*(1-flow)+(j===n.to?flow:0);
   cumulative+=weight;
   film.hidden=weight<=.000001&&j!==n.to;
   if(film.hidden)return;
   // Weighted compositing preserves the current picture when another click redirects it.
   film.style.zIndex=String(j+1);film.style.opacity=cumulative>0?weight/Math.max(weight,cumulative):0;
   const end=j===n.to?[0,0,0,1]:[snapshot.pose[0]-n.dir*.8,snapshot.pose[1]-.1,snapshot.pose[2],snapshot.pose[3]+.012];
   film.style.transform=freeScroll()?'none':poseString(snapshot.pose.map((v,k)=>mix(v,end[k],flow)));
   if(j===n.to)windows[j].style.inset=baseInsets(j).map((v,k)=>mix(n.inset[k]||0,v,flow).toFixed(3)+'%').join(' ');
  });
  const out=1-ease(u/.5),enter=ease((u-.5)/.5);
  panels.forEach((panel,j)=>{
   const start=n.copy[j],alpha=j===n.to?mix(start.alpha,1,enter):start.alpha*out;
   panel.hidden=alpha<.00001;panel.inert=j!==n.to||alpha<.5;
   if(panel.hidden){panel.classList.toggle('is-entered',false);return}
   panel.classList.toggle('is-navigation-copy',true);panel.classList.toggle('is-entered',true);
   panel.style.opacity=alpha;
   panel.style.transform='none';
   paint(articles[j],'pointerEvents',panel.inert?'none':'auto');
  });
  labelCurrent.textContent=n.label;labelIncoming.textContent=`0${n.to+1} / ${names[n.to]}`;
  paint(label,'width',mix(n.labelWidth,labelWidths[n.to],flow).toFixed(3)+'px');
  labelCurrent.style.opacity=1-flow;labelCurrent.style.transform=`translate3d(0,${-flow*10}px,0)`;
  labelIncoming.style.opacity=flow;labelIncoming.style.transform=`translate3d(0,${(1-flow)*10}px,0)`;
  const selected=flow>.5?n.to:n.from;
  label.setAttribute('aria-label',flow>.5?labelIncoming.textContent:n.label);
  notice.hidden=!states[n.to].failed;
  if(journey.dataset.moment!==String(selected))journey.dataset.moment=String(selected);
  if(journey.dataset.scene!==names[selected])journey.dataset.scene=names[selected];
  if(u===1){
   const resume=n.resume&&!state.failed;
   // Do not trust a play request made while this layer was hidden/transparent.
   // Commit the selected scene first, then restart a paused or pending decoder
   // once while retaining a successfully playing clip's native clock.
   if(resume&&(v.paused||state.playPending||state.blocked)){
    pauseNative(n.to);state.blocked=false;state.retryAfter=0;
   }
   finishNavigation();
   if(resume)startPlayback();
  }
  wake();
 }
 function draw(now){
  raf=0;if(shellPaused||mobileOffscreen)return;const dt=Math.min(48,now-(previousTime||now-16));previousTime=now;
  if(catalogScroll){drawCatalogScroll(now);return}
  if(navigation){drawNavigation(dt);return}
  let completed=false,completeToShopping=false;
  waitingForNext=false;
  if(settling){
   settling.elapsed+=dt;
   const progress=clamp(settling.elapsed/SETTLE_DURATION);
   current=target=mix(settling.from,settling.to,ease(progress));
   // The stage stays pinned while the timeline settles. Keep the native scroll
   // position in sync so the next gesture continues from this exact frame.
   if(!freeScroll()){scrollTo({top:Math.max(0,bounds().top+current*scrollSpan),behavior:'instant'});lastScrollY=scrollY}
   if(progress===1)settling=null;
  }else if(manualPlaying){
   const v=videos[activeFilm],i=activeFilm;
   if(playingFilm===i&&v.readyState>=2&&!v.seeking){
    let phase=(v.currentTime/duration(i))*(1+(i?LEAD:0))-(i?LEAD:0);
    if(i<LAST&&phase>=HANDOFF){
     const next=i+1;
     // Both native decoders run at normal speed during page playback.
     const ready=startNative(next,incomingTime(i,Math.min(phase,1)),incomingRate(i));
     if(!ready&&!states[next].failed){
      waitingForNext=true;pauseNative(i);
      // Hold the last composed frame while the next decoder becomes ready.
      phase=current*EXTENT-i;
     }
     if(phase>=1&&(ready||states[next].failed)){
      pauseNative(i);activeFilm=next;playingFilm=states[next].native?next:-1;
      if(states[next].native){videos[next].playbackRate=1;current=target=clamp((next+Math.max(0,(videos[next].currentTime/duration(next))*(1+LEAD)-LEAD))/EXTENT)}
      else current=target=next/EXTENT;
     }else current=target=clamp((i+Math.max(0,phase))/EXTENT);
    }else if(phase>=1&&i===LAST){current=target=1;completeToShopping=handoffEligible;stopPlayback();completed=true}
    else current=target=clamp((i+Math.max(0,phase))/EXTENT);
   }
  }else{
   current+=(target-current)*(1-Math.exp(-dt/105));
   if(Math.abs(target-current)<.000045)current=target;
  }
  // Avoid landing in the departing chapter through floating-point rounding.
  const position=current*EXTENT,i=Math.min(LAST,Math.floor(position+1e-7)),t=Math.max(0,position-i);
  shown=i;const f=order[i],next=order[i+1],transition=i<LAST?ease((t-HANDOFF)/(1-HANDOFF)):0;
  activeFilm=f;loadFilm(f);if(manualPlaying&&states[f].failed)stopPlayback();if(t>.25&&f<LAST)loadFilm(f+1);
  films.forEach((film,j)=>{
   const incoming=j===next&&transition>0;
   // Expose the next compositing layer, still transparent, before its dissolve begins.
   const prepared=j===next&&manualPlaying&&t>=HANDOFF-.14,visible=j===f||incoming||prepared;
   if(film.hidden===visible)film.hidden=!visible;
   if(!visible){if(states[j].native)pauseNative(j);return}
   paint(film,'zIndex',j===next?'2':'1');
   paint(film,'opacity',j===next?transition:1);
   paint(film,'transform',freeScroll()?'none':j===next?`translate3d(${(1-transition)*1.2}%,0,0) scale(${mix(1.025,1,transition)})`:`translate3d(${-transition*.6}%,0,0) scale(${1+transition*.012})`);
   setWindow(j);
   // Scroll mode scrubs both films. In Play story, pump leaves native clips alone.
   if(incoming)seek(j,incomingTime(i,t));
  });
  seek(f,clipProgress(f,t)*duration(f));
  if(manualPlaying)playCurrent();
  panels.forEach((panel,j)=>{
   if(freeScroll()){
    // Fade text out before the next scene enters; never move its reading rows.
    const visible=j===i||(j===next&&transition>0),alpha=j===i?1-ease(transition*2):j===next?ease((transition-.5)*2):0;
    if(panel.hidden===visible)panel.hidden=!visible;
    panel.classList.toggle('is-entered',visible);panel.classList.toggle('is-navigation-copy',visible);
    paint(panel,'opacity',alpha);paint(panel,'transform','none');
    panel.inert=alpha<.5;paint(articles[j],'pointerEvents',alpha>.5?'auto':'none');return;
   }
   if(panel.hidden!==(j!==i))panel.hidden=j!==i;
   if(j!==i&&panel.inert!==true)panel.inert=true;
   if(j!==i){panel.classList.toggle('is-entered',false);panel.classList.toggle('is-navigation-copy',false);return}
   // A failed clip can stop at the first frame, before its normal copy entrance.
   const fallback=states[f].failed;
   const enter=fallback||i===0?1:ease(t/.17),leave=fallback||i===LAST?1:1-ease((t-.55)/.17),alpha=enter*leave;
   panel.classList.toggle('is-entered',alpha>.035);
   paint(panel,'opacity',alpha);
   paint(panel,'transform','none');
   if(panel.inert!==(alpha<.2))panel.inert=alpha<.2;paint(articles[j],'pointerEvents',alpha>.5?'auto':'none');
  });
  const currentName=`0${f+1} / ${names[f]}`,nextName=next===undefined?'':`0${next+1} / ${names[next]}`;
  paint(label,'width',mix(labelWidths[f],labelWidths[next??f],transition).toFixed(3)+'px');
  if(labelCurrent.textContent!==currentName)labelCurrent.textContent=currentName;
  if(labelIncoming.textContent!==nextName)labelIncoming.textContent=nextName;
  paint(labelCurrent,'opacity',1-transition);
  paint(labelCurrent,'transform',`translate3d(0,${-transition*10}px,0)`);
  paint(labelIncoming,'opacity',transition);
  paint(labelIncoming,'transform',`translate3d(0,${(1-transition)*10}px,0)`);
  const announced=transition>.5?nextName:currentName;
  if(label.getAttribute('aria-label')!==announced)label.setAttribute('aria-label',announced);
  const selectedChapter=transition>.5?next:f;
  setSelectedChapter(selectedChapter);notice.hidden=!states[f].failed;
  chapters.forEach((button,j)=>{
   const progress=j<f?1:j===f?t:0;
   setChapterProgress(j,progress);
  });
  drawChapterDots(f,next,transition);
  if(journey.dataset.moment!==String(i))journey.dataset.moment=String(i);
  if(journey.dataset.scene!==names[f])journey.dataset.scene=names[f];
  syncPlaybackControl();
  if(completed){
   if(freeScroll())showCompletionCue();
   else if(completeToShopping&&!reduced&&canRemindExplore()){
    // Begin the anchor on the completion frame, with no closing hold or extra timer.
    scrollToShopping();if(catalogScroll)catalogScroll.started=now;
   }else showExploreCue();
  }
  if(current!==target||manualPlaying||settling)wake();else previousTime=0;
 }
  function go(i){
   // Tapping the selected mobile chapter must not silently pause its film.
   if(freeScroll()&&!reduced&&!navigation&&i<=LAST&&chapters[i].getAttribute('aria-current')==='step'){
    if(!manualPlaying||!mediaIsPlaying()){if(current>=.998)current=target=(i+.31)/EXTENT;startPlayback(true)}
    return;
   }
   cancelSettle();
  cancelAutoplay();catalogScroll=null;stopPlayback();dismissExploreCue();
  if(i>LAST){finishNavigation();scrollToShopping();return}
  if(reduced){shown=i;activeFilm=order[i];setSelectedChapter(activeFilm);drawChapterDots(activeFilm,activeFilm,0);document.getElementById('still-'+i).scrollIntoView({behavior:'instant'});return}
  // A fresh selection is also an explicit retry after an earlier network error.
  if(states[i].failed){states[i].failed=false;states[i].loaded=false}
  states[i].abortRetries=0;states[i].retryAfter=0;
  beginNavigation(i);
  // Request the selected clip in the click/touchend event, not 420ms later.
  // This retains user activation on Safari when automatic playback is limited.
  if(navigation&&freeScroll())startNative(i,states[i].desired,1,true);
  syncPlaybackControl();
  if(!navigation)scheduleExploreCue();
 }
  function setReduced(value){
   cancelSettle();
  cancelAutoplay();catalogScroll=null;finishNavigation();reduced=value;stopPlayback();cancelAnimationFrame(raf);raf=0;
  scrollAnchor=null;scrollSpan=0;mobileOffscreen=false;paint(journey,'height','');
  document.body.classList.toggle('is-reduced',value);stills.hidden=!value;
  if(value)dismissExploreCue();
  if(toggle){toggle.hidden=false;toggle.querySelector('.site-motion-status').textContent=value?'On':'Off';toggle.setAttribute('aria-checked',String(value))}play.disabled=value;
  videos.forEach((_,i)=>pauseNative(i));
  syncCopyRows();refreshBounds();measureChapters();
  if(value)drawChapterDots(activeFilm,activeFilm,0);
  if(!value){measure();current=target;wake()}
 }
 chapters.forEach((b,i)=>{
  b.addEventListener('click',()=>go(i));
  b.addEventListener('pointerenter',()=>loadFilm(i));
  b.addEventListener('focus',()=>loadFilm(i));
 });
 chapterSelect.addEventListener('change',()=>go(+chapterSelect.value));
 cue.addEventListener('click',()=>freeScroll()?scrollToShopping(true):go(shown+1));
 toggle?.addEventListener('click',()=>{
  // Switching the page's reading mode must keep the visitor at the footer.
  const before=toggle.getBoundingClientRect().top;
  setReduced(!reduced);
  const shift=toggle.getBoundingClientRect().top-before;
  if(Math.abs(shift)>.5)scrollTo({top:Math.max(0,scrollY+shift),behavior:'instant'});
 });
  function startPlayback(fromGesture=false){
   cancelSettle();
  cancelAutoplay();cancelHandoff();catalogScroll=null;if(reduced)return;
  if(freeScroll())dismissExploreCue();
  finishNavigation();
  if(current>=.998){current=target=0;activeFilm=0}
  const s=states[activeFilm];if(s.failed){s.failed=false;s.loaded=false}
  anchorScroll();handoffEligible=!freeScroll()&&current<.001;
  if(fromGesture){videos.forEach((_,i)=>{pauseNative(i);states[i].abortRetries=0;states[i].retryAfter=0});mobileOffscreen=false}
  manualPlaying=false;playingFilm=-1;loadFilm(activeFilm);
  if(freeScroll()){
   // Commit the selected picture before an immediate Play request. A tap can
   // arrive during a swipe transition while that picture is still hidden.
   target=current;cancelAnimationFrame(raf);raf=0;draw(performance.now());
  }
  manualPlaying=true;
  // Align replay/resume before the immediate request, including a previously
  // completed opening clip. Keep Play taps inside the user-activation event.
  states[activeFilm].desired=clipProgress(activeFilm,current*EXTENT-activeFilm)*duration(activeFilm);
  if(startNative(activeFilm,states[activeFilm].desired,1,true))playingFilm=activeFilm;
  if(fromGesture&&freeScroll()&&activeFilm<LAST){
   const phase=current*EXTENT-activeFilm;
   if(phase>=HANDOFF){
    // An interrupted dissolve has two visible clips. Restart both in this tap,
    // before Safari's gesture authorization expires in a later animation frame.
    const next=activeFilm+1;
    loadFilm(next);startNative(next,incomingTime(activeFilm,Math.min(phase,1)),incomingRate(activeFilm),true);
   }
  }
  syncPlaybackControl();notifyPlayback();showExploreCue();wake();
 }
 const togglePlayback=()=>{
  cancelAutoplay();
  if((manualPlaying||navigation?.resume)&&mediaIsPlaying()){stopPlayback();wake()}else startPlayback(true);
 };
 let playTouch=null,ignorePlayClickUntil=0;
 play.addEventListener('touchstart',e=>{
  const t=e.touches.length===1?e.touches[0]:null;
  playTouch=t?{id:t.identifier,x:t.clientX,y:t.clientY}:null;
 },{passive:true});
 play.addEventListener('touchcancel',()=>{playTouch=null},{passive:true});
 play.addEventListener('touchend',e=>{
  const start=playTouch;playTouch=null;if(!start||e.touches.length)return;
  const t=[...e.changedTouches].find(t=>t.identifier===start.id);
  if(!t||Math.hypot(t.clientX-start.x,t.clientY-start.y)>12)return;
  // The real touchend owns this activation; suppress its synthetic click so
  // one tap cannot immediately play and then pause the same clip.
  e.preventDefault();ignorePlayClickUntil=performance.now()+600;togglePlayback();
 },{passive:false});
 play.addEventListener('click',e=>{
  if(e?.detail&&performance.now()<ignorePlayClickUntil)return;
  togglePlayback();
 });
  addEventListener('scroll',()=>{
  const previousY=lastScrollY;lastScrollY=scrollY;
  if(shellPaused||Math.abs(scrollY-previousY)<.5)return;
  if(catalogScroll){if(Math.abs(scrollY-catalogScroll.lastY)<2)return;catalogScroll=null}
  if(freeScroll()&&!reduced){
   cancelHandoff();dismissExploreCue();
   syncPageVisibility();scheduleAutoplay();
   return;
  }
  cancelAutoplay();cancelSettle();scrollDirection=scrollY>previousY?1:-1;
  if(navigation)navigation.followScroll=true;
  stopPlayback(previousY);restoreScrollTimeline(previousY);
  const b=bounds();if(scrollY>=b.top+b.range+b.height-.5)restoreStoryHistory();
  measure();scheduleSettle();
 },{passive:true});
 // Direct visitor input takes priority over the story's own scroll updates.
 const interruptStory=(restore=true)=>{cancelHandoff();cancelSettle();if(shellPaused)return;catalogScroll=null;if(freeScroll()&&!reduced){dismissExploreCue();scheduleAutoplay();return}cancelAutoplay();if(navigation)navigation.followScroll=true;stopPlayback();if(restore)restoreScrollTimeline();measure();scheduleSettle();resetExploreCue()};
 addEventListener('wheel',e=>{
  if(!e.deltaY||e.ctrlKey)return;
  scrollDirection=e.deltaY>0?1:-1;interruptStory(false);
  const b=bounds();
  if(e.cancelable&&!shellPaused&&!reduced&&!freeScroll()&&scrollAnchor&&scrollY>=b.top-2&&scrollY<=b.top+b.range+2){
   // Consume only the handoff event, applying its full delta once. Letting a
   // native wheel race a scroll rebase drops that first delta in some browsers.
   e.preventDefault();
   restoreScrollTimeline(scrollY,e.deltaY*(e.deltaMode===1?16:e.deltaMode===2?b.height:1));
   measure();scheduleSettle();
  }
 },{passive:false});
 const cancelHandoffOnInput=()=>{cancelHandoff();if(catalogScroll){catalogScroll=null;measure()}};
 addEventListener('pointerdown',cancelHandoffOnInput,{passive:true});
 addEventListener('keydown',cancelHandoffOnInput);
 addEventListener('touchstart',e=>{if(!freeScroll())cancelAutoplay();cancelHandoff();if(catalogScroll||!e.target.closest('button,a,input,select,textarea'))interruptStory()},{passive:true});
 // Horizontal gestures select one chapter. Native vertical scrolling and pinch
 // zoom remain available; edge gestures and interactive controls are excluded.
 let swipe=null;
 addEventListener('touchstart',e=>{
  swipe=null;
  if(!framedMobile()||reduced||shellPaused||e.touches?.length!==1||!e.target.closest('.stage')||e.target.closest('button,a,input,select,textarea'))return;
  const touch=e.touches[0];
  if(touch.clientX<24||touch.clientX>innerWidth-24)return;
  swipe={id:touch.identifier,x:touch.clientX,y:touch.clientY,started:performance.now(),axis:null};
 },{passive:true});
 addEventListener('touchmove',e=>{
  if(!swipe)return;
  if(e.touches.length!==1){swipe=null;return}
  const touch=e.touches[0];
  if(touch.identifier!==swipe.id){swipe=null;return}
  const dx=Math.abs(touch.clientX-swipe.x),dy=Math.abs(touch.clientY-swipe.y);
  if(!swipe.axis&&Math.max(dx,dy)>10){
   if(dx>dy*1.35)swipe.axis='x';else swipe=null;
  }
 },{passive:true});
 addEventListener('touchend',e=>{
  const gesture=swipe;swipe=null;
  if(!gesture||gesture.axis!=='x'||e.touches.length||reduced||shellPaused||performance.now()-gesture.started>1000)return;
  const touch=[...e.changedTouches].find(t=>t.identifier===gesture.id);if(!touch)return;
  const dx=touch.clientX-gesture.x,dy=touch.clientY-gesture.y;
  if(Math.abs(dx)<Math.max(44,Math.min(80,innerWidth*.14))||Math.abs(dx)<Math.abs(dy)*1.35)return;
  const from=navigation?.to??+chapterSelect.value,to=clamp(from+(dx<0?1:-1),0,LAST);
  if(to!==from)go(to);
 },{passive:true});
 addEventListener('touchcancel',()=>{swipe=null},{passive:true});
 addEventListener('keydown',e=>{if((catalogScroll||!e.target.closest('button,a,input,select,textarea'))&&['ArrowDown','ArrowUp','PageDown','PageUp','Home','End',' ','Escape'].includes(e.key))interruptStory()});
  addEventListener('resize',()=>{
  cancelHandoff();cancelSettle();catalogScroll=null;
  if(wasFramed!==freeScroll()){
   wasFramed=freeScroll();mobileOffscreen=false;scrollAnchor=null;scrollSpan=0;paint(journey,'height','');
  }
  if(!reduced&&!shellPaused&&scrollY<=bounds().top+bounds().range+1)anchorScroll();
  syncCopyRows();refreshBounds();
  if(shellPaused){measureChapters();return}
  // Mobile browser chrome/orientation can resize the viewport mid-swipe.
  // Preserve the selected chapter's playback intent when ending its dissolve,
  // including if the browser also pauses its decoder during that layout change.
  const resumeAfterResize=!!navigation?.resume;
  finishNavigation();measureChapters();
  if(!reduced){if(resumeAfterResize)startPlayback();else{wake();scheduleSettle()}}
 },{passive:true});
 addEventListener('keydown',e=>{if(shellPaused||e.target.closest('input,textarea,select,button,a,.storefront-content,.site-footer'))return;if(e.key==='ArrowRight'){e.preventDefault();go(shown+1)}if(e.key==='ArrowLeft'){e.preventDefault();go(Math.max(0,shown-1))}});
 document.addEventListener('site:overlay',e=>{
  shellPaused=!!e.detail.open;
  if(shellPaused){cancelAutoplay();cancelHandoff();cancelSettle();dismissExploreCue();catalogScroll=null;videos.forEach((_,i)=>pauseNative(i));playingFilm=-1;cancelAnimationFrame(raf);raf=0;previousTime=0}
  else{refreshBounds();if(manualPlaying){showExploreCue();wake()}else{measure();scheduleSettle()}pump(activeFilm)}
 });
 document.addEventListener('visibilitychange',()=>{if(document.hidden){cancelSettle();clearTimeout(autoplayTimer);autoplayTimer=0;dismissExploreCue();catalogScroll=null;finishNavigation();stopPlayback();videos.forEach(v=>v.pause());cancelAnimationFrame(raf);raf=0;previousTime=0}else{refreshBounds();measure();scheduleSettle();pump(activeFilm);scheduleAutoplay()}});
 mediaQuery.addEventListener('change',e=>setReduced(e.matches));
 setReduced(reduced);
 // Respect restored scroll position and system motion preferences on arrival.
 autoplayPending=journey.dataset.autoplay==='true'&&!reduced&&(freeScroll()||openingInView());
 scheduleAutoplay();
 if(typeof ResizeObserver!=='undefined'){
  new ResizeObserver(()=>{measureChapters();wake()}).observe(chapterNav);
  new ResizeObserver(()=>{refreshBounds();wake()}).observe(journey);
  new ResizeObserver(()=>{syncCopyRows();if(manualPlaying)anchorScroll();refreshBounds();wake()}).observe(stage);
 }
 if(document.fonts?.ready)document.fonts.ready.then(()=>{syncCopyRows();refreshBounds();measureChapters();wake()});
 addEventListener('pageshow',()=>{refreshBounds();if(!manualPlaying)measure();scheduleAutoplay()});
})();

  (() => {
 'use strict';
 const preference=matchMedia('(prefers-reduced-motion: reduce)');
 const reduced=()=>preference.matches||document.body.classList.contains('is-reduced');
 const loader=document.querySelector('.experience-loader'),opening=document.querySelector('.film video');
 let loaderDone=false,showTimer=0,limitTimer=0,hideTimer=0;
 function finishLoading(){
  if(loaderDone)return;
  loaderDone=true;clearTimeout(showTimer);clearTimeout(limitTimer);
  document.body.classList.remove('is-loading');
  if(!loader)return;
  loader.classList.add('is-complete');
  // The indicator never traps input or waits for the entire story download.
  hideTimer=setTimeout(()=>{loader.hidden=true},reduced()?0:240);
 }
 if(loader&&opening&&!reduced()&&opening.readyState<2){
  document.body.classList.add('is-loading');
  showTimer=setTimeout(()=>{if(!loaderDone)loader.hidden=false},120);
  limitTimer=setTimeout(finishLoading,1200);
  opening.addEventListener('loadeddata',finishLoading,{once:true});
  opening.addEventListener('error',finishLoading,{once:true});
  addEventListener('wheel',finishLoading,{once:true,passive:true});
  addEventListener('pointerdown',finishLoading,{once:true,passive:true});
  addEventListener('keydown',finishLoading,{once:true});
 }else finishLoading();
 preference.addEventListener('change',()=>{if(reduced())finishLoading()});
 addEventListener('pagehide',()=>{finishLoading();clearTimeout(hideTimer);if(loader)loader.hidden=true},{once:true});

 const rail=document.querySelector('.commerce-grid');
 if(!rail)return;
 const cards=[...rail.querySelectorAll('.commerce-card-shell')];
 const previous=document.querySelector('.rail-previous'),next=document.querySelector('.rail-next');
 let paint=0;
 const maximum=()=>Math.max(0,rail.scrollWidth-rail.clientWidth);
 const positions=()=>cards.map(card=>card.offsetLeft-cards[0].offsetLeft);
 function update(){
  paint=0;const left=rail.scrollLeft,max=maximum();
  previous.disabled=left<=2;next.disabled=left>=max-2;
 }
 function schedule(){if(!paint)paint=requestAnimationFrame(update)}
 function move(direction){
  const stops=positions(),left=rail.scrollLeft;
  const destination=direction>0?stops.find(x=>x>left+4):stops.slice().reverse().find(x=>x<left-4);
  rail.scrollTo({left:Math.max(0,Math.min(maximum(),destination??(direction>0?maximum():0))),behavior:reduced()?'instant':'smooth'});
  schedule();
 }
 previous.addEventListener('click',()=>move(-1));
 next.addEventListener('click',()=>move(1));
 rail.addEventListener('scroll',schedule,{passive:true});
 rail.addEventListener('keydown',event=>{
  if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;
  event.preventDefault();event.stopPropagation();
  if(event.key==='Home'||event.key==='End'){
   rail.scrollTo({left:event.key==='Home'?0:maximum(),behavior:reduced()?'instant':'smooth'});schedule();
  }else move(event.key==='ArrowRight'?1:-1);
 });
 addEventListener('resize',schedule,{passive:true});
 if(typeof ResizeObserver!=='undefined')new ResizeObserver(schedule).observe(rail);
 update();
})();

 }
 customElements.define('tt-film-hero',TTFilmHero);
})();
